import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArchiveQueryForm } from '../../components/Satellite/ArchiveQueryForm';
import { SatelliteImageTask } from '../../models/satellite';
import StandardPage from '../../components/StandardPage';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { SatelliteImageRequestTable } from '../../components/Satellite/SatelliteImageRequestTable';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';
import { trackSatelliteTaskRefresh, trackSatelliteTaskSubmitted } from '../../analytics';

export default function Tasks() {
  const [results, setResults] = useState<SatelliteImageTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<number | null>(null);

  const safeAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    safeAbort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/satellite/get-tasks`,
        { headers: { Authorization: `Bearer ${token}` }, signal: abortController.signal }
      );
      setResults(Array.isArray(data.tasks) ? data.tasks : []);
    } catch (err) {
      const e = err as Error;
      if (e.name !== 'AbortError' && (err as any).code !== 'ERR_CANCELED') toast.error(handleApiError(err));  
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchTasks();
    return () => safeAbort();
  }, [fetchTasks]);

  useEffect(() => {
    if (!polling) {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    intervalRef.current = window.setInterval(() => {
      if (!loading) fetchTasks();
    }, 15000);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [polling, loading, fetchTasks]);

  const handleSubmit = async (params: {
    taskName: string;
    latitude: number;
    longitude: number;
    datetime?: string;
    provider?: string;
  }) => {
    const requestBody = {
      name: params.taskName,
      datetime: params.datetime || '',
      latitude: params.latitude.toString(),
      longitude: params.longitude.toString(),
      resolution: '',
      provider: params.provider || 'microsoft-pc',
      source_url: '',
    };

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/satellite/create-task`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      trackSatelliteTaskSubmitted({
        provider: requestBody.provider || 'microsoft-pc',
        has_datetime: Boolean(requestBody.datetime),
      });
      setResults(prev => [
        { ...requestBody, id: new Date().toISOString(), status: 'PENDING', org_name: '', user_email: '', is_paid: false, task_cost: 0 },
        ...prev,
      ]);
      toast.success('Task submitted successfully.');
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardPage title='Satellite Image Tasks' marginBottom={10}>      
      <div className="p-6">
        <ArchiveQueryForm onSubmit={handleSubmit} loading={loading} />
      </div>

      <div className="flex items-center justify-end p-6">
        <button
          onClick={() => {
            trackSatelliteTaskRefresh();
            fetchTasks();
          }}
          disabled={loading}
          className={`bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-full shadow-md hover:from-blue-600 hover:to-blue-800 transition ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          title="Refresh now"
        >
          Refresh
        </button>
      </div>

      <div className="p-6 pt-0">
        <SatelliteImageRequestTable tasks={results} setTasks={setResults} loading={loading} />
      </div>
    </StandardPage>
  );
}
